import { onBeforeUnmount, watch, type Ref } from 'vue';

/**
 * The open/close mechanics an in-flow anchored popover needs, factored out of
 * `Menu.vue` (component-lib) so `DemoStageBar`'s two pill-half popovers (the
 * persona picker and the journey card) get the same house behaviour without a
 * component-lib change: a click outside the popover's own root closes it
 * without moving focus (the click already moved it), Escape closes it AND
 * returns focus to the element that opened it.
 *
 * Deliberately NOT a focus trap (`useOverlayA11y.useFocusTrap`, component-lib):
 * these are in-flow popovers anchored under a pill half, not teleported
 * dialogs — same family as `Menu.vue`'s own panel, which does not trap Tab
 * either.
 */
export function useAnchoredPopover(
  open: Ref<boolean>,
  rootRef: Ref<HTMLElement | null>,
  close: (returnFocus: boolean) => void,
): void {
  function onPointerDown(e: Event) {
    const target = e.target as Node | null;
    if (rootRef.value && target && !rootRef.value.contains(target)) close(false);
  }
  function isInside(node: Node | null): boolean {
    return !!(rootRef.value && node && rootRef.value.contains(node));
  }
  function onKeydown(e: KeyboardEvent) {
    if (e.key !== 'Escape') return;
    // Only act when the Escape belongs to this popover — its target or the
    // currently focused element sits inside `rootRef`. Otherwise leave the
    // event alone entirely: a capture-phase listener that always stops
    // propagation would swallow Escape for the rest of the page (e.g. a
    // sidebar panel) while this popover happens to be open elsewhere.
    const target = e.target as Node | null;
    if (!isInside(target) && !isInside(document.activeElement)) return;
    e.stopPropagation();
    close(true);
  }
  function addListeners() {
    if (typeof document === 'undefined') return;
    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeydown, true);
  }
  function removeListeners() {
    if (typeof document === 'undefined') return;
    document.removeEventListener('pointerdown', onPointerDown, true);
    document.removeEventListener('keydown', onKeydown, true);
  }
  watch(open, (isOpen) => (isOpen ? addListeners() : removeListeners()));
  onBeforeUnmount(removeListeners);
}
